import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError, setToken } from "../api";
import { useAuth } from "../context/AuthContext";
import { GraduationCap, Briefcase, BookOpen } from "lucide-react";

const ROLE_OPTIONS = [
  { key: "student",   label: "Student",   icon: GraduationCap, color: "#006CFF", sub: "Apply to jobs, earn CoE credentials." },
  { key: "company",   label: "Hiring at a company", icon: Briefcase, color: "#9933FF", sub: "Post roles, hire verified talent." },
  { key: "institution", label: "Institution", icon: BookOpen, color: "#0099FF", sub: "Place your students, end-to-end." },
];

const FRIENDLY_ERR = {
  invalid_state: "Your sign-in session expired. Please try again.",
  token_exchange: "LinkedIn rejected the token exchange. Check the LinkedIn app credentials and redirect URI.",
  no_access_token: "LinkedIn returned no access token.",
  userinfo: "Couldn't fetch your LinkedIn profile.",
  missing_claims: "LinkedIn didn't share your email — make sure your LinkedIn email is verified.",
  email_unverified: "Please verify your primary email address on LinkedIn (Settings → Sign in & security → Email addresses) and try again.",
  server_not_configured: "LinkedIn sign-in isn't configured on this server yet.",
  token_network: "Couldn't reach LinkedIn. Try again in a moment.",
  userinfo_network: "Couldn't reach LinkedIn. Try again in a moment.",
};

function parseFragment() {
  const hash = (typeof window !== "undefined" && window.location.hash) || "";
  if (!hash.startsWith("#")) return {};
  const params = new URLSearchParams(hash.slice(1));
  const out = {};
  for (const [k, v] of params.entries()) out[k] = v;
  return out;
}

export default function LinkedInAuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const ran = useRef(false);
  const watchdog = useRef(null);

  const [phase, setPhase] = useState("loading"); // loading | role-chooser | error
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // Watchdog: if the handshake stalls (hung network, silent failure) never
    // leave the user staring at an endless spinner — flip to a retryable error.
    watchdog.current = setTimeout(() => {
      setPhase((p) => {
        if (p === "loading") {
          setErr("This is taking longer than expected. Your connection may have dropped.");
          return "error";
        }
        return p;
      });
    }, 15000);

    (async () => {
      const frag = parseFragment();
      if (frag.error) {
        clearTimeout(watchdog.current);
        setErr(FRIENDLY_ERR[frag.error] || frag.desc || frag.error);
        setPhase("error");
        return;
      }
      if (!frag.token) {
        clearTimeout(watchdog.current);
        setErr("Sign-in did not return a token.");
        setPhase("error");
        return;
      }
      setToken(frag.token);
      // Clear the hash so a refresh doesn't reprocess it
      window.history.replaceState({}, "", window.location.pathname);

      try {
        const { data: me } = await api.get("/auth/me");
        clearTimeout(watchdog.current);
        setUser(me);
        if (frag.needs_role === "1") {
          // First-time LinkedIn signup with no role hint — show chooser
          setPhase("role-chooser");
          return;
        }
        nav(`/${me.role}/dashboard`, { replace: true });
      } catch (e) {
        clearTimeout(watchdog.current);
        setErr(formatError(e) || "Couldn't load your profile.");
        setPhase("error");
      }
    })();

    return () => clearTimeout(watchdog.current);
  }, [nav, setUser]);

  // Restart the LinkedIn handshake from scratch so a stalled/failed attempt
  // is one tap away from recovery instead of a dead end.
  const retryLinkedIn = async () => {
    setRetrying(true); setErr("");
    try {
      const { data } = await api.get("/auth/linkedin/start");
      if (!data?.authorization_url) throw new Error("No authorization URL");
      window.location.href = data.authorization_url;
    } catch (e) {
      setRetrying(false);
      setErr(formatError(e) || "Couldn't restart LinkedIn sign-in. Please try again.");
    }
  };

  const chooseRole = async (role) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/linkedin/choose-role", { role });
      setUser(data.user);
      nav(`/${data.user.role}/dashboard`, { replace: true });
    } catch (e) {
      setBusy(false);
      setErr(formatError(e) || "Couldn't set your role. Please try again.");
      setPhase("error");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-4" data-testid="linkedin-callback">
      <div className="card card-pad max-w-lg w-full">
        {phase === "loading" && (
          <div className="text-center">
            <span className="eyebrow">Finishing sign-in</span>
            <div className="font-display font-bold text-2xl mt-3">Connecting your LinkedIn account…</div>
            <div className="mt-4 text-sm text-muted">One sec — verifying with LinkedIn.</div>
          </div>
        )}

        {phase === "role-chooser" && (
          <div>
            <span className="eyebrow">One last step</span>
            <h1 className="font-display font-extrabold text-2xl md:text-3xl mt-2">Which best describes you?</h1>
            <p className="text-sm text-muted mt-2">Pick a role to finish setting up your SkillConnect account.</p>
            <div className="mt-6 space-y-3">
              {ROLE_OPTIONS.map(r => {
                const RIcon = r.icon;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => chooseRole(r.key)}
                    disabled={busy}
                    data-testid={`linkedin-role-${r.key}`}
                    className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl border-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-12px_rgba(0,40,168,0.35)] active:scale-[0.99] disabled:opacity-60"
                    style={{ borderColor: r.color }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                      style={{ background: `${r.color}15`, color: r.color }}
                    >
                      <RIcon size={20} />
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-bold" style={{ color: r.color }}>{r.label}</div>
                      <div className="text-xs text-muted mt-0.5">{r.sub}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {err && <p className="text-red-600 text-sm mt-4" data-testid="linkedin-role-error">{err}</p>}
          </div>
        )}

        {phase === "error" && (
          <div className="text-center">
            <span className="eyebrow">Sign-in failed</span>
            <div className="font-display font-bold text-2xl mt-3">We couldn&apos;t finish your LinkedIn sign-in</div>
            <p className="text-sm text-red-600 mt-3" data-testid="linkedin-callback-error">{err}</p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={retryLinkedIn}
                disabled={retrying}
                data-testid="linkedin-retry-btn"
              >
                {retrying ? "Reconnecting…" : "Try again"}
              </button>
              <button
                className="btn-secondary w-full sm:w-auto"
                onClick={() => nav("/login", { replace: true })}
                data-testid="linkedin-back-to-login-btn"
              >
                Back to login
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
